import React, { useEffect, useMemo, useState, useRef } from "react";
import {
    Box,
    Typography,
    Paper,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Divider,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    Card,
    CardContent,
    FormLabel,
    Radio,
    RadioGroup,
    FormControlLabel,
    Snackbar,
    SnackbarContent,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import SendIcon from "@material-ui/icons/Send";
import CloseIcon from "@material-ui/icons/Close";
import MessageIcon from "@material-ui/icons/Message";
import NoteService from "./Locality/Service/noteService";
import StudentService from "./Locality/Service/studentService";
import SocketService from "./Locality/Service/socketService";
import AddClassService from "./Locality/Service/addClassService";
import SectionService from "./Locality/Service/sectionService";
import PageTitle from "../../components/PageTitle/PageTitle";
import Widget from "../../components/Widget/Widget";
import { useFormik } from "formik";
import * as Yup from "yup";

/** Relative last-seen label from presence.lastSeenAt (e.g. "1 hour ago"). */
const formatLastSeenLabel = (lastSeenAt) => {
    if (!lastSeenAt) return "Not available";
    const seen = new Date(lastSeenAt);
    if (Number.isNaN(seen.getTime())) return "Not available";

    const diffMs = Math.max(0, Date.now() - seen.getTime());
    const minutes = Math.floor(diffMs / 60000);
    if (minutes < 1) return "Just now";
    if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;

    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;

    const days = Math.floor(hours / 24);
    if (days < 30) return `${days} day${days === 1 ? "" : "s"} ago`;

    const months = Math.floor(days / 30);
    if (months < 12) return `${months} month${months === 1 ? "" : "s"} ago`;

    const years = Math.floor(days / 365);
    return `${years} year${years === 1 ? "" : "s"} ago`;
};

/** Status meanings shown in the student list legend. */
const PRESENCE_STATUS_MEANINGS = {
    // online: {
    //     label: 'Online',
    //     meaning: 'Parent is currently active on the mobile app.',
    //     color: '#2e7d32',
    // },
    unavailable: {
        label: 'Not available',
        meaning: 'Parent app is not connected or presence data is unavailable.',
        color: '#9e9e9e',
    },
    // offline: {
    //     label: 'Last seen',
    //     meaning: 'Parent was active recently but is offline now.',
    //     color: '#ed6c02',
    // },
};

/**
 * presence null → Not available; isOnline → Online; else Last seen {time}.
 * Returns text, online flag, status key, and meaning for legend/tooltips.
 */
const getStudentPresenceDisplay = (student) => {
    const presence = student?.presence;
    if (presence == null) {
        return {
            text: PRESENCE_STATUS_MEANINGS.unavailable.label,
            online: false,
            status: 'unavailable',
            meaning: PRESENCE_STATUS_MEANINGS.unavailable.meaning,
        };
    }
    if (presence.isOnline) {
        return {
            text: PRESENCE_STATUS_MEANINGS.online.label,
            online: true,
            status: 'online',
            meaning: PRESENCE_STATUS_MEANINGS.online.meaning,
        };
    }
    const lastSeen = formatLastSeenLabel(presence.lastSeenAt);
    if (lastSeen === "Not available") {
        return {
            text: PRESENCE_STATUS_MEANINGS.unavailable.label,
            online: false,
            status: 'unavailable',
            meaning: PRESENCE_STATUS_MEANINGS.unavailable.meaning,
        };
    }
    return {
        text: `Last seen ${lastSeen}`,
        online: false,
        status: 'offline',
        meaning: PRESENCE_STATUS_MEANINGS.offline.meaning,
    };
};

/** Convenience label used in student list rows. */
const getPresenceLabel = (student) => getStudentPresenceDisplay(student).text;

function PresenceStatusLegend() {
    const classes = useStyles();
    return (
        <Box className={classes.presenceLegend}>
            <Typography className={classes.presenceLegendTitle}>Status meanings</Typography>
            {Object.entries(PRESENCE_STATUS_MEANINGS).map(([key, item]) => (
                <Box key={key} className={classes.presenceLegendRow}>
                    <span
                        className={classes.presenceLegendDot}
                        style={{ backgroundColor: item.color }}
                        aria-hidden
                    />
                    <Box>
                        <Typography className={classes.presenceLegendLabel}>{item.label}</Typography>
                        <Typography className={classes.presenceLegendMeaning}>{item.meaning}</Typography>
                    </Box>
                </Box>
            ))}
        </Box>
    );
}

const useStyles = makeStyles((theme) => ({
    yellowHeader: {
        backgroundColor: '#FFC107',
        color: 'white',
        padding: '16px 24px',
        marginBottom: '24px',
        borderRadius: '4px 4px 0 0',
    },
    layout: {
        display: 'flex',
        gap: 16,
        minHeight: 600,
        alignItems: 'stretch',
        [theme.breakpoints.down('sm')]: {
            flexDirection: 'column',
        },
    },
    studentsPanel: {
        width: 300,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        height: 'min(600px, calc(100vh - 220px))',
        maxHeight: 'min(600px, calc(100vh - 220px))',
        [theme.breakpoints.down('sm')]: {
            width: '100%',
            height: 'min(400px, 50vh)',
            maxHeight: 'min(400px, 50vh)',
        },
    },
    studentsPanelHeader: {
        flexShrink: 0,
    },
    studentsListScroll: {
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
    },
    notesPanel: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
    },
    notesContainer: {
        flex: 1,
        overflowY: 'auto',
        padding: 16,
        backgroundColor: '#f5f5f5',
    },
    noteBubble: {
        position: 'relative',
        padding: '14px 18px',
        borderRadius: 14,
        marginBottom: 12,
        backgroundColor: '#fffbe6',
        border: '1px solid #ffe38a',
        color: '#5b4407',
        cursor: 'pointer',
        boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
        transition: 'transform 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease',
        '&:hover': {
            backgroundColor: '#fff3bf',
            boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
            transform: 'translateY(-1px)',
        },
    },
    noteBubbleUnread: {
        backgroundColor: '#fff5d6',
        borderColor: '#ffc107',
        boxShadow: '0 1px 4px rgba(255,193,7,0.25)',
    },
    noteHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        marginBottom: 8,
        flexWrap: 'wrap',
    },
    noteType: {
        fontWeight: 700,
        fontSize: '0.85rem',
        marginBottom: 6,
    },
    noteText: {
        fontSize: '0.9rem',
        marginBottom: 6,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    noteTime: {
        fontSize: '0.7rem',
        opacity: 0.75,
    },
    noteRowMeta: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
        marginTop: 4,
    },
    unreadDot: {
        minWidth: 20,
        height: 20,
        padding: '0 6px',
        borderRadius: 10,
        backgroundColor: '#e53935',
        color: 'white',
        fontSize: '0.7rem',
        fontWeight: 700,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    studentRow: {
        position: 'relative',
        paddingRight: 40,
    },
    studentRowName: {
        display: 'block',
        fontSize: '0.95rem',
    },
    studentRowMeta: {
        display: 'block',
        fontSize: '0.75rem',
        color: '#666',
        marginTop: 2,
    },
    studentRowPresence: {
        display: 'block',
        fontSize: '0.7rem',
        color: '#888',
        marginTop: 2,
    },
    studentRowPresenceOnline: {
        color: '#2e7d32',
        fontWeight: 600,
    },
    studentRowPresenceUnavailable: {
        color: '#757575',
        fontWeight: 500,
    },
    studentRowPresenceOffline: {
        color: '#ed6c02',
        fontWeight: 500,
    },
    presenceStatusDot: {
        display: 'inline-block',
        width: 8,
        height: 8,
        borderRadius: '50%',
        marginRight: 6,
        flexShrink: 0,
        verticalAlign: 'middle',
    },
    presenceLegend: {
        marginTop: 12,
        padding: '10px 12px',
        borderRadius: 8,
        backgroundColor: '#f8fafc',
        border: '1px solid #e8edf3',
    },
    presenceLegendTitle: {
        fontSize: '0.72rem',
        fontWeight: 700,
        color: '#475569',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        marginBottom: 8,
    },
    presenceLegendRow: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: 8,
        marginBottom: 8,
        '&:last-child': {
            marginBottom: 0,
        },
    },
    presenceLegendDot: {
        width: 8,
        height: 8,
        borderRadius: '50%',
        marginTop: 5,
        flexShrink: 0,
    },
    presenceLegendLabel: {
        fontSize: '0.75rem',
        fontWeight: 700,
        color: '#334155',
        lineHeight: 1.3,
    },
    presenceLegendMeaning: {
        fontSize: '0.7rem',
        color: '#64748b',
        lineHeight: 1.35,
    },
    studentRowDot: {
        position: 'absolute',
        right: 8,
        top: '50%',
        transform: 'translateY(-50%)',
        minWidth: 22,
        height: 22,
        padding: '0 7px',
        borderRadius: 11,
        backgroundColor: '#e53935',
        color: 'white',
        fontSize: '0.75rem',
        fontWeight: 700,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    chatDialog: {
        '& .MuiDialog-paper': {
            width: '500px',
            maxWidth: '90vw',
            height: '600px',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
        },
    },
    chatHeader: {
        backgroundColor: '#2196F3',
        color: 'white',
        padding: '16px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    chatMessages: {
        flex: 1,
        overflowY: 'auto',
        padding: 16,
        backgroundColor: '#f5f5f5',
    },
    chatMessageBubble: {
        padding: '10px 16px',
        borderRadius: 18,
        marginBottom: 12,
        maxWidth: '70%',
        wordWrap: 'break-word',
    },
    messageSent: {
        backgroundColor: '#2196F3',
        color: 'white',
        marginLeft: 'auto',
        textAlign: 'right',
    },
    messageReceived: {
        backgroundColor: '#e0e0e0',
        color: '#000',
        marginRight: 'auto',
    },
    chatInput: {
        padding: 16,
        borderTop: '1px solid #e0e0e0',
        display: 'flex',
        gap: 8,
        alignItems: 'center',
    },
    sectionTitle: {
        color: '#FFC107',
        fontWeight: 'bold',
        marginBottom: '12px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
    },
    yellowButton: {
        backgroundColor: '#FFC107',
        color: 'white',
        padding: '16px',
        fontSize: '18px',
        fontWeight: 'bold',
        textTransform: 'uppercase',
        width: '100%',
        marginTop: '8px',
        '&:hover': {
            backgroundColor: '#FFB300',
        },
    },
    composeCard: {
        padding: 8,
        boxShadow: 'none',
    },
    clickableBadge: {
        cursor: 'pointer',
        display: 'inline-flex',
        borderRadius: 8,
        padding: 4,
        transition: 'background-color 0.15s ease',
        '&:hover': {
            backgroundColor: 'rgba(15, 59, 98, 0.08)',
        },
    },
    clickableBadgeLight: {
        cursor: 'pointer',
        display: 'inline-flex',
        borderRadius: 8,
        padding: 4,
        transition: 'background-color 0.15s ease',
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
        },
    },
    unreadPanelHint: {
        fontSize: '0.78rem',
        color: '#64748b',
        marginTop: 4,
    },
}));

const SCHOOL_NOTIFICATION_SOUND = `${process.env.PUBLIC_URL || ''}/sound/universfield-new-notification-051-494246.mp3`;

export default function NoteList() {
    const classes = useStyles();
    const userDetails = useMemo(() => JSON.parse(localStorage.getItem("userDetail") || "{}"), []);

    const [studentsList, setStudentsList] = useState([]);
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [notesList, setNotesList] = useState([]);
    const [chatOpen, setChatOpen] = useState(false);
    const [selectedNote, setSelectedNote] = useState(null);
    const [chatMessages, setChatMessages] = useState([]);
    const [chatInput, setChatInput] = useState('');
    const [unreadCounts, setUnreadCounts] = useState({}); // { noteId: count }
    const [studentUnreadCounts, setStudentUnreadCounts] = useState({}); // { studentId: totalCount }
    const totalSchoolChatUnread = useMemo(
        () => Object.values(unreadCounts).reduce((a, v) => a + (Number(v) || 0), 0),
        [unreadCounts],
    );

    const formatNewCount = (count) => {
        const n = Number(count) || 0;
        return n > 99 ? "99+" : String(n);
    };
    const [classesList, setClassesList] = useState([]);
    const [sectionsList, setSectionsList] = useState([]);
    const [filterClassId, setFilterClassId] = useState("");
    const [filterSectionId, setFilterSectionId] = useState("");
    const [composeOpen, setComposeOpen] = useState(false);
    const [composeStudent, setComposeStudent] = useState(null);
    const [toast, setToast] = useState({ open: false, type: 'success', message: '' });
    const socketInitializedRef = useRef(false);
    const chatMessagesEndRef = useRef(null);
    const groupedDataMapRef = useRef(new Map()); // Store grouped notes data
    const groupedStudentDetailsRef = useRef(new Map()); // studentId -> student summary
    const chatOpenRef = useRef(false);
    const selectedNoteRef = useRef(null);
    /** noteId -> studentId. Used to attribute unread bumps to the right student row. */
    const noteToStudentRef = useRef(new Map());
    const unreadCountsRef = useRef({});
    const studentUnreadCountsRef = useRef({});
    const loadNotesRequestRef = useRef(0);
    const notificationAudioRef = useRef(null);
    const [showUnreadStudentsOnly, setShowUnreadStudentsOnly] = useState(false);

    const isSchooleRole = useMemo(
        () => String(userDetails?.role || '').toUpperCase() === 'SCHOOLE',
        [userDetails?.role],
    );

    useEffect(() => {
        if (!isSchooleRole) return;
        notificationAudioRef.current = new Audio(SCHOOL_NOTIFICATION_SOUND);
        notificationAudioRef.current.preload = 'auto';
    }, [isSchooleRole]);

    const playSchoolNotificationSound = () => {
        if (!isSchooleRole) return;
        try {
            const audio = notificationAudioRef.current;
            if (!audio) return;
            audio.currentTime = 0;
            const playPromise = audio.play();
            if (playPromise && typeof playPromise.catch === 'function') {
                playPromise.catch(() => {});
            }
        } catch (err) {
            // Browser may block autoplay until user interacts with the page
        }
    };

    useEffect(() => {
        unreadCountsRef.current = unreadCounts;
    }, [unreadCounts]);
    useEffect(() => {
        studentUnreadCountsRef.current = studentUnreadCounts;
    }, [studentUnreadCounts]);

    useEffect(() => {
        chatOpenRef.current = chatOpen;
    }, [chatOpen]);
    useEffect(() => {
        selectedNoteRef.current = selectedNote;
    }, [selectedNote]);

    const canComposeForStudent = () => {
        const r = String(userDetails?.role || "").toUpperCase();
        return r === "SCHOOLE" || r === "TEACHER" || r === "SUPERADMIN";
    };

    const applyUnreadSummaryData = (res) => {
        const data = res?.data || res || {};
        const normalized = {};
        Object.entries(data.byNoteId || {}).forEach(([noteId, count]) => {
            const n = Number(count) || 0;
            if (n > 0) normalized[String(noteId)] = n;
        });
        setUnreadCounts(normalized);
        setStudentUnreadCounts(data.byStudentId || {});
        const noteStudentMap = data.noteIdToStudentId || {};
        Object.entries(noteStudentMap).forEach(([noteId, studentId]) => {
            if (noteId && studentId) {
                noteToStudentRef.current.set(String(noteId), String(studentId));
            }
        });
    };

    const clearUnreadForNote = (noteId) => {
        const id = String(noteId);
        setUnreadCounts((prev) => {
            const next = { ...prev };
            delete next[id];
            return next;
        });
    };

    const persistNoteRead = async (noteId) => {
        if (!noteId) return;
        try {
            await NoteService.markNoteRead(String(noteId));
        } catch (err) {
            console.error('Error marking note as read:', err);
        }
    };

    const handleUnreadUpdate = (payload) => {
        const noteId = payload?.noteId ? String(payload.noteId) : '';
        if (!noteId) return;

        if (
            chatOpenRef.current &&
            selectedNoteRef.current &&
            String(selectedNoteRef.current._id) === noteId
        ) {
            persistNoteRead(noteId);
            return;
        }

        const studentId = payload?.studentId || noteToStudentRef.current.get(noteId);
        if (studentId) {
            noteToStudentRef.current.set(noteId, String(studentId));
        }

        if (payload.unreadCount != null) {
            const count = Number(payload.unreadCount) || 0;
            setUnreadCounts((prev) => {
                const next = { ...prev };
                if (count <= 0) delete next[noteId];
                else next[noteId] = count;
                return next;
            });
            if (count > 0) playSchoolNotificationSound();
            return;
        }

        const inc = Number(payload.increment) || 1;
        setUnreadCounts((prev) => ({
            ...prev,
            [noteId]: (Number(prev[noteId]) || 0) + inc,
        }));
        playSchoolNotificationSound();
    };

    const handleNewNote = (payload) => {
        if (!payload?.noteId || !payload?.studentId) return;
        const senderRole = String(payload.senderRole || '').toUpperCase();
        if (senderRole !== 'PARENT') return;

        const sid = String(payload.studentId);
        const noteCard = {
            _id: payload.noteId,
            noteType: payload.noteType,
            note: payload.note,
            createdAt: payload.createdAt,
            messageKind: payload.messageKind || 'NOTE',
            studentId: { _id: sid, firstName: payload.studentName },
        };
        mergeCreatedNoteIntoCache(sid, noteCard);
        noteToStudentRef.current.set(String(payload.noteId), sid);

        if (payload.studentName) {
            groupedStudentDetailsRef.current.set(sid, {
                _id: sid,
                firstName: payload.studentName.split(' ')[0] || payload.studentName,
                lastName: payload.studentName.split(' ').slice(1).join(' ') || '',
            });
        }

        const studentLabel = payload.studentName || 'Student';
        const preview = payload.preview || payload.noteType || 'New message';
        setToast({
            open: true,
            type: 'success',
            message: `${studentLabel}: ${preview}`,
        });
    };

    const openComposeForStudent = (student) => {
        if (!student?._id || !canComposeForStudent()) return;
        setComposeStudent(student);
        setSelectedStudent(student);
        setComposeOpen(true);
    };

    const mergeCreatedNoteIntoCache = (studentId, createdNote) => {
        if (!studentId || !createdNote?._id) return;
        const sid = String(studentId);
        const prev = groupedDataMapRef.current.get(sid) || [];
        const withoutDup = prev.filter((n) => String(n._id) !== String(createdNote._id));
        const next = [createdNote, ...withoutDup];
        groupedDataMapRef.current.set(sid, next);
        // Keep the noteId -> studentId map current so unread bumps land on the right row.
        noteToStudentRef.current.set(String(createdNote._id), sid);
        if (selectedStudent?._id && String(selectedStudent._id) === sid) {
            setNotesList(next);
        }
    };

    const composeFormik = useFormik({
        initialValues: { noteType: '', note: '' },
        enableReinitialize: true,
        validationSchema: Yup.object().shape({
            noteType: Yup.string().required('Please select Note Type'),
            note: Yup.string(),
        }),
        onSubmit: async (values, { resetForm }) => {
            const student = composeStudent || selectedStudent;
            if (!student?._id) return;
            try {
                const classRef = student.classId;
                const classId = typeof classRef === 'object' ? classRef?._id : classRef;
                const schoolRef = student.schooleId || userDetails?.schooleId || userDetails?.schoolId;
                const schooleId = typeof schoolRef === 'object' ? schoolRef?._id : schoolRef;

                const body = {
                    messageKind: 'NOTE',
                    noteType: values.noteType,
                    note: values.note || '',
                    studentId: student._id,
                };
                if (classId) body.classId = classId;
                if (schooleId) body.schooleId = schooleId;

                const res = await NoteService.createNote(body);
                const createdNote = res?.data || res;
                if (createdNote?._id) {
                    mergeCreatedNoteIntoCache(student._id, createdNote);
                }
                resetForm();
                setComposeOpen(false);
                setComposeStudent(null);
                setToast({ open: true, type: 'success', message: 'Message sent successfully.' });
            } catch (err) {
                setToast({
                    open: true,
                    type: 'error',
                    message: err.response?.data?.message || err?.message || 'Error sending message',
                });
            }
        },
    });

    useEffect(() => {
        loadStudents();

        // Initialize socket connection
        if (!socketInitializedRef.current) {
            SocketService.initializeSocket();
            socketInitializedRef.current = true;
        }

        // Seed unread counts ONCE from a single aggregated endpoint.
        NoteService.getUnreadSummary()
            .then(applyUnreadSummaryData)
            .catch((err) => console.error('Error fetching unread summary:', err));

        const role = String(userDetails?.role || '').toUpperCase();
        if (['SCHOOLE', 'TEACHER', 'SUPERADMIN'].includes(role)) {
            SocketService.joinSchoolNoteFeed();
        }

        const offUpdate = SocketService.onUnreadUpdate(handleUnreadUpdate);

        const offCleared = SocketService.onUnreadCleared((payload) => {
            if (!payload?.noteId) return;
            clearUnreadForNote(payload.noteId);
        });

        const offNewNote = SocketService.onNewNote(handleNewNote);

        return () => {
            if (typeof offUpdate === 'function') offUpdate();
            if (typeof offCleared === 'function') offCleared();
            if (typeof offNewNote === 'function') offNewNote();
            socketInitializedRef.current = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (selectedStudent) {
            loadNotesForStudent(selectedStudent._id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedStudent]);

    useEffect(() => {
        const role = String(userDetails?.role || "").toUpperCase();
        const schooleId = userDetails?.schooleId || userDetails?.schoolId;
        if (!(role === "SCHOOLE" || role === "SUPERADMIN") || !schooleId) return;
        AddClassService.getAllAddClass(schooleId, "SCHOOLE")
            .then((res) => setClassesList(Array.isArray(res) ? res : res?.data || []))
            .catch(() => setClassesList([]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userDetails]);

    useEffect(() => {
        const role = String(userDetails?.role || "").toUpperCase();
        const schooleId = userDetails?.schooleId || userDetails?.schoolId;
        if (!(role === "SCHOOLE" || role === "SUPERADMIN") || !schooleId) return;
        if (!filterClassId) {
            setSectionsList([]);
            setFilterSectionId("");
            return;
        }
        SectionService.findSectionList({ schooleId, classId: filterClassId, status: true })
            .then((res) => {
                const data = res?.data ?? res;
                setSectionsList(Array.isArray(data) ? data : []);
            })
            .catch(() => setSectionsList([]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterClassId]);

    const loadStudents = async () => {
        try {
            const schooleId = userDetails?.schooleId || userDetails?.schoolId;
            if (!schooleId) return;

            const role = String(userDetails?.role || "").toUpperCase();

            const groupedData = (await NoteService.getNotesGroupedByStudent()) || [];

            const groupedDataMap = new Map();
            const noteToStudent = new Map();
            groupedData.forEach((group) => {
                const studentId = group.studentDetails.studentId?._id || group.studentDetails.studentId;
                if (studentId) {
                    const sid = String(studentId);
                    const notes = group.studentNotes || [];
                    const firstNote = notes[0] || null;
                    groupedDataMap.set(sid, notes);
                    groupedStudentDetailsRef.current.set(sid, {
                        _id: studentId,
                        firstName: group.studentDetails.firstName,
                        lastName: group.studentDetails.lastName,
                        classId: group.studentDetails.classId || firstNote?.classId || null,
                        sectionId: firstNote?.sectionId || null,
                    });
                    notes.forEach((n) => {
                        if (n?._id) noteToStudent.set(String(n._id), sid);
                    });
                }
            });
            groupedDataMapRef.current = groupedDataMap;
            noteToStudentRef.current = noteToStudent;

            if (role === "SCHOOLE" || role === "SUPERADMIN") {
                try {
                    const allStudentsRes = await StudentService.findStudentList({ schooleId });
                    const allStudents = allStudentsRes?.data || allStudentsRes || [];
                    const list = Array.isArray(allStudents) ? allStudents : [];
                    setStudentsList(list);
                    if (list.length > 0 && !selectedStudent && !showUnreadStudentsOnly) {
                        setSelectedStudent(list[0]);
                    }
                } catch (e) {
                    setStudentsList([]);
                }
                return;
            }

            if (groupedData.length === 0) {
                setStudentsList([]);
                return;
            }

            const studentsWithMessages = groupedData.map((group) => {
                const studentId = group.studentDetails.studentId?._id || group.studentDetails.studentId;
                const firstNote = group.studentNotes && group.studentNotes.length > 0 ? group.studentNotes[0] : null;
                return {
                    _id: studentId,
                    firstName: group.studentDetails.firstName,
                    lastName: group.studentDetails.lastName,
                    classId: firstNote?.classId || null,
                    notes: group.studentNotes || [],
                };
            });

            setStudentsList(studentsWithMessages);
            if (studentsWithMessages.length > 0 && !selectedStudent && !showUnreadStudentsOnly) {
                setSelectedStudent(studentsWithMessages[0]);
            }
        } catch (err) {
            console.error('Error fetching students:', err);
            setStudentsList([]);
        }
    };

    const getStudentClassId = (student) => {
        const c = student?.classId;
        if (!c) return "";
        return typeof c === "object" ? String(c._id || "") : String(c);
    };

    const getStudentSectionId = (student) => {
        const s = student?.sectionId;
        if (!s) return "";
        return typeof s === "object" ? String(s._id || "") : String(s);
    };

    const getStudentUnreadCount = (studentId) => {
        const sid = String(studentId);
        let total = 0;
        Object.entries(unreadCounts).forEach(([noteId, count]) => {
            if (noteToStudentRef.current.get(String(noteId)) === sid) {
                total += Number(count) || 0;
            }
        });
        return total;
    };

    const resolveStudentRecord = (studentId) => {
        const sid = String(studentId);
        const fromList = (studentsList || []).find((item) => String(item?._id) === sid);
        if (fromList) return fromList;

        const fromGrouped = groupedStudentDetailsRef.current.get(sid);
        if (fromGrouped) return fromGrouped;

        return { _id: studentId, firstName: "Student", lastName: "" };
    };

    const unreadStudents = useMemo(() => {
        const totalsByStudent = new Map();

        Object.entries(unreadCounts).forEach(([noteId, count]) => {
            if (Number(count) <= 0) return;
            const studentId = noteToStudentRef.current.get(String(noteId));
            if (!studentId) return;
            const sid = String(studentId);
            totalsByStudent.set(sid, (totalsByStudent.get(sid) || 0) + Number(count));
        });

        return Array.from(totalsByStudent.entries())
            .filter(([, total]) => total > 0)
            .sort((a, b) => b[1] - a[1])
            .map(([studentId]) => resolveStudentRecord(studentId));
        // noteToStudentRef is a ref; unreadCounts drive recomputation
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [studentsList, unreadCounts]);

    const unreadStudentCount = unreadStudents.length;

    const handleShowUnreadStudents = () => {
        if (totalSchoolChatUnread <= 0 || unreadStudentCount <= 0) return;
        setShowUnreadStudentsOnly(true);
        setFilterClassId("");
        setFilterSectionId("");
        setSelectedStudent(unreadStudents[0]);
    };

    const renderNewMessagesChip = () => {
        if (showUnreadStudentsOnly || totalSchoolChatUnread <= 0) return null;
        return (
            <Chip
                size="small"
                label={`${formatNewCount(totalSchoolChatUnread)} new`}
                onClick={handleShowUnreadStudents}
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleShowUnreadStudents();
                    }
                }}
                role="button"
                tabIndex={0}
                aria-label="View students with new messages"
                style={{
                    backgroundColor: "#e53935",
                    color: "#fff",
                    fontWeight: 700,
                    height: 22,
                    cursor: "pointer",
                }}
            />
        );
    };

    /** School admin: show student list only after class is chosen, and section when class has sections */
    const adminStudentListVisible = useMemo(() => {
        const role = String(userDetails?.role || "").toUpperCase();
        if (!(role === "SCHOOLE" || role === "SUPERADMIN")) return true;
        if (!filterClassId) return false;
        if (sectionsList.length > 0 && !filterSectionId) return false;
        return true;
    }, [userDetails, filterClassId, filterSectionId, sectionsList.length]);

    const filteredStudents = useMemo(() => {
        const role = String(userDetails?.role || "").toUpperCase();
        if ((role === "SCHOOLE" || role === "SUPERADMIN") && !adminStudentListVisible) {
            return [];
        }
        if (!(role === "SCHOOLE" || role === "SUPERADMIN")) return studentsList;
        return (studentsList || []).filter((st) => {
            if (filterClassId && getStudentClassId(st) !== String(filterClassId)) return false;
            if (filterSectionId && getStudentSectionId(st) !== String(filterSectionId)) return false;
            return true;
        });
    }, [studentsList, userDetails, filterClassId, filterSectionId, adminStudentListVisible]);

    const displayStudents = useMemo(() => {
        if (showUnreadStudentsOnly) {
            const list = [...unreadStudents];
            if (selectedStudent?._id) {
                const selectedId = String(selectedStudent._id);
                if (!list.some((student) => String(student?._id) === selectedId)) {
                    list.unshift(selectedStudent);
                }
            }
            return list;
        }

        const role = String(userDetails?.role || "").toUpperCase();
        if ((role === "SCHOOLE" || role === "SUPERADMIN") && !adminStudentListVisible) {
            return selectedStudent?._id ? [selectedStudent] : [];
        }

        return filteredStudents;
    }, [showUnreadStudentsOnly, unreadStudents, filteredStudents, selectedStudent, adminStudentListVisible, userDetails?.role]);

    const isSchoolAdminRole = ["SCHOOLE", "SUPERADMIN"].includes(String(userDetails?.role || "").toUpperCase());
    const hideStudentPicker =
        isSchoolAdminRole &&
        !showUnreadStudentsOnly &&
        !adminStudentListVisible &&
        !selectedStudent?._id;

    const applyFiltersForSelectedStudent = () => {
        if (!selectedStudent?._id) return;
        const classId = getStudentClassId(selectedStudent);
        const sectionId = getStudentSectionId(selectedStudent);
        if (classId && !filterClassId) setFilterClassId(classId);
        if (sectionId && !filterSectionId) setFilterSectionId(sectionId);
    };

    const handleShowAllStudents = () => {
        applyFiltersForSelectedStudent();
        setShowUnreadStudentsOnly(false);
    };

    useEffect(() => {
        if (!showUnreadStudentsOnly || unreadStudents.length === 0) return;
        if (!selectedStudent?._id) {
            setSelectedStudent(unreadStudents[0]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showUnreadStudentsOnly, unreadStudents]);

    useEffect(() => {
        const role = String(userDetails?.role || "").toUpperCase();
        if (!(role === "SCHOOLE" || role === "SUPERADMIN")) return;
        if (showUnreadStudentsOnly) return;

        if (!filteredStudents || filteredStudents.length === 0) {
            // Keep the current student visible while class/section filters are unset.
            if (selectedStudent?._id) return;
            setSelectedStudent(null);
            setNotesList([]);
            return;
        }

        const currentId = selectedStudent?._id ? String(selectedStudent._id) : null;
        const stillExists = currentId
            ? filteredStudents.some((s) => String(s?._id) === currentId)
            : false;

        if (!stillExists) {
            setSelectedStudent(filteredStudents[0]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterClassId, filterSectionId, filteredStudents, showUnreadStudentsOnly]);

    const loadNotesForStudent = async (studentId) => {
        try {
            if (!studentId) return;

            const sid = String(studentId);
            const requestId = ++loadNotesRequestRef.current;
            let notes = [];

            const cached = groupedDataMapRef.current?.get(sid);
            if (Array.isArray(cached) && cached.length > 0) {
                notes = [...cached];
            } else {
                const schooleId = userDetails?.schooleId || userDetails?.schoolId;
                try {
                    const body = { studentId: sid };
                    if (schooleId) body.schooleId = schooleId;
                    const res = await NoteService.findNoteList(body);
                    notes = Array.isArray(res) ? res : res?.data || [];
                } catch {
                    const classId = userDetails?.role === 'TEACHER' ? userDetails?.classId : null;
                    const res = await NoteService.getAllNotes(classId);
                    notes = (res || []).filter((note) => {
                        const noteStudentId = note?.studentId?._id || note?.studentId;
                        return String(noteStudentId) === sid;
                    });
                }

                const byId = new Map(notes.map((n) => [String(n._id), n]));
                const unread = unreadCountsRef.current || {};
                const missingIds = Object.entries(unread)
                    .filter(([noteId, count]) => {
                        if (Number(count) <= 0) return false;
                        const mappedStudent = noteToStudentRef.current.get(String(noteId));
                        return mappedStudent === sid;
                    })
                    .map(([noteId]) => noteId)
                    .filter((id) => !byId.has(String(id)));

                if (missingIds.length > 0) {
                    const fetched = await Promise.all(
                        missingIds.map((id) =>
                            NoteService.getNoteById(id)
                                .then((r) => r?.data || r)
                                .catch(() => null)
                        )
                    );
                    fetched.forEach((n) => {
                        if (n?._id) byId.set(String(n._id), n);
                    });
                }

                notes = Array.from(byId.values()).sort(
                    (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
                );
                groupedDataMapRef.current.set(sid, notes);
            }

            if (requestId !== loadNotesRequestRef.current) return;

            notes.forEach((n) => {
                if (n?._id) noteToStudentRef.current.set(String(n._id), sid);
            });
            setNotesList(notes);
        } catch (err) {
            console.error('Error fetching notes:', err);
        }
    };

    const loadChatMessages = async (noteId) => {
        try {
            if (!noteId) return;
            const res = await NoteService.getChatMessagesByNoteId(noteId);
            setChatMessages(res || []);
        } catch (err) {
            console.error('Error fetching chat messages:', err);
        }
    };

    const markNotesAsRead = async (noteIds) => {
        const ids = (noteIds || []).map((id) => String(id)).filter(Boolean);
        if (ids.length === 0) return;

        ids.forEach((noteId) => clearUnreadForNote(noteId));
        await Promise.all(ids.map((noteId) => persistNoteRead(noteId)));
    };

    const handleSelectStudent = (student) => {
        if (!student?._id) return;
        setSelectedStudent(student);
        loadNotesForStudent(student._id);
    };

    const handleOpenChat = async (note) => {
        setSelectedNote(note);
        setChatOpen(true);
        if (!note?._id) return;

        await markNotesAsRead([note._id]);

        // Load initial chat messages
        await loadChatMessages(note._id);

        // Join socket room for real-time updates while the dialog is open
        SocketService.joinNoteChat(note._id, {
            onJoined: (data) => {
                console.log('Joined room:', data);
            },
            onMessage: (messageData) => {
                setChatMessages((prev) => {
                    const exists = prev.some((msg) => msg._id === messageData._id);
                    if (exists) return prev;
                    return [...prev, messageData];
                });
                setTimeout(() => {
                    if (chatMessagesEndRef.current) {
                        chatMessagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
                    }
                }, 100);
            },
            onError: (error) => {
                console.error('Socket error:', error);
            },
        });
    };

    const handleCloseChat = () => {
        const noteToMark = selectedNote;

        // Leave socket room
        if (noteToMark?._id) {
            SocketService.leaveNoteChat(noteToMark._id);
        }
        setChatOpen(false);
        setSelectedNote(null);
        setChatMessages([]);
        setChatInput('');
    };

    const handleSendMessage = async () => {
        try {
            const text = (chatInput || '').trim();
            if (!text) return;
            if (!selectedNote?._id) return;

            const studentId = selectedNote?.studentId?._id || selectedNote?.studentId || selectedStudent?._id;
            if (!studentId) return;

            setChatInput('');

            // Try sending via socket first (real-time)
            if (SocketService.isConnected()) {
                SocketService.sendMessage(selectedNote._id, text, {
                    onSuccess: () => {
                        markNotesAsRead([selectedNote._id]);
                    },
                    onError: (error) => {
                        console.error('Socket send error, falling back to REST:', error);
                        sendViaRestAPI(studentId, text);
                    },
                });
            } else {
                await sendViaRestAPI(studentId, text);
            }
        } catch (err) {
            console.error('Error sending message:', err);
        }
    };

    const sendViaRestAPI = async (studentId, text) => {
        try {
            await NoteService.sendChatMessage({
                studentId,
                note: text,
                parentNoteId: selectedNote._id,
            });
            await loadChatMessages(selectedNote._id);
            await markNotesAsRead([selectedNote._id]);
            setTimeout(() => {
                if (chatMessagesEndRef.current) {
                    chatMessagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
                }
            }, 100);
        } catch (err) {
            console.error('Error sending via REST API:', err);
        }
    };

    const getStudentName = (student) => {
        if (!student) return '-';
        const first = student.firstName || '';
        const last = student.lastName || '';
        return `${first} ${last}`.trim() || '-';
    };

    const getStudentNameFromNote = (note) => {
        const s = note?.studentId;
        if (!s) return '-';
        const first = s.firstName || '';
        const last = s.lastName || '';
        return `${first} ${last}`.trim() || '-';
    };

    const getClassName = (student) => {
        return student?.classId?.className || '-';
    };

    const getClassNameFromNote = (note) => {
        return note?.studentId?.classId?.className || note?.classId?.className || '-';
    };

    const formatTime = (dateString) => {
        if (!dateString) return '';
        const d = new Date(dateString);
        if (Number.isNaN(d.getTime())) return '';
        return d.toLocaleString('en-US', { 
            year: 'numeric', 
            month: 'short', 
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatTimeShort = (dateString) => {
        if (!dateString) return '';
        const d = new Date(dateString);
        if (Number.isNaN(d.getTime())) return '';
        return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    };

    const isMine = (msg) => {
        const me = userDetails?._id;
        const sender = msg?.createdBy?._id;
        return Boolean(me && sender && String(me) === String(sender));
    };

    return (
        <>
            <PageTitle title="Notes List" />
            <div className={classes.yellowHeader}>
                <Typography variant="h5" style={{ fontWeight: 'bold', color: 'white' }}>
                    Messages from Parents
                </Typography>
            </div>

            <Widget>
                <Box className={classes.layout}>
                    {/* Left Panel - Students List */}
                    <Paper className={classes.studentsPanel}>
                        <Box p={2} className={classes.studentsPanelHeader}>
                            <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gridGap={8}>
                                <Box>
                                    <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
                                        {showUnreadStudentsOnly ? "Unread Students" : "Students"}
                                    </Typography>
                                    {showUnreadStudentsOnly && (
                                        <Typography className={classes.unreadPanelHint}>
                                            {unreadStudentCount} student{unreadStudentCount === 1 ? "" : "s"} with new messages
                                        </Typography>
                                    )}
                                </Box>
                                {renderNewMessagesChip()}
                            </Box>
                            {showUnreadStudentsOnly && (
                                <Box mt={1.5}>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={handleShowAllStudents}
                                        style={{ textTransform: "none" }}
                                    >
                                        Show all students
                                    </Button>
                                </Box>
                            )}
                            {["SCHOOLE", "SUPERADMIN"].includes(String(userDetails?.role || "").toUpperCase()) &&
                                !showUnreadStudentsOnly && (
                                <Box mt={2} display="flex" flexDirection="column" gridGap={12}>
                                    <FormControl variant="outlined" fullWidth size="small">
                                        <InputLabel id="note-list-filter-class">Class</InputLabel>
                                        <Select
                                            labelId="note-list-filter-class"
                                            value={filterClassId}
                                            onChange={(e) => {
                                                setFilterClassId(e.target.value);
                                                setFilterSectionId("");
                                                setSectionsList([]);
                                                setShowUnreadStudentsOnly(false);
                                            }}
                                            label="Class"
                                        >
                                            <MenuItem value="">
                                                <em>All classes</em>
                                            </MenuItem>
                                            {classesList.map((c) => (
                                                <MenuItem key={c._id} value={c._id}>
                                                    {c.className}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    {filterClassId && sectionsList.length > 0 && (
                                        <FormControl variant="outlined" fullWidth size="small">
                                            <InputLabel id="note-list-filter-section">Section</InputLabel>
                                            <Select
                                                labelId="note-list-filter-section"
                                                value={filterSectionId}
                                                onChange={(e) => {
                                                    setFilterSectionId(e.target.value);
                                                    setShowUnreadStudentsOnly(false);
                                                }}
                                                label="Section"
                                            >
                                                <MenuItem value="">
                                                    <em>All sections</em>
                                                </MenuItem>
                                                {sectionsList.map((s) => (
                                                    <MenuItem key={s._id} value={s._id}>
                                                        {s.sectionName}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    )}
                                </Box>
                            )}
                            {isSchoolAdminRole && !showUnreadStudentsOnly && (
                                <PresenceStatusLegend />
                            )}
                        </Box>
                        {isSchoolAdminRole &&
                            !showUnreadStudentsOnly &&
                            hideStudentPicker && (
                                <Box px={2} pb={2}>
                                    <Typography variant="body2" style={{ color: "#777" }}>
                                        {!filterClassId
                                            ? "Select a class to view students."
                                            : "Select a section to view students."}
                                    </Typography>
                                </Box>
                            )}
                        <Divider
                            style={{
                                flexShrink: 0,
                                display: hideStudentPicker ? "none" : undefined,
                            }}
                        />
                        <Box
                            className={classes.studentsListScroll}
                            style={{
                                display: hideStudentPicker ? "none" : undefined,
                            }}
                        >
                            <List dense component="div" disablePadding>
                            {displayStudents.length === 0 ? (
                                <Box p={2}>
                                    <Typography variant="body2" style={{ color: '#777' }}>
                                        {showUnreadStudentsOnly
                                            ? "No new messages right now."
                                            : !["SCHOOLE", "SUPERADMIN"].includes(String(userDetails?.role || "").toUpperCase()) ||
                                              adminStudentListVisible
                                                ? studentsList.length === 0
                                                    ? "No students found."
                                                    : "No students match the selected class or section."
                                                : ""}
                                    </Typography>
                                </Box>
                            ) : (
                                displayStudents.map((student) => {
                                    const selected = selectedStudent?._id && student._id
                                        ? String(selectedStudent._id) === String(student._id)
                                        : false;
                                    const studentIdStr = student._id ? String(student._id) : String(student);
                                    const unreadCount = getStudentUnreadCount(studentIdStr);
                                    const hasUnread = unreadCount > 0;
                                    return (
                                        <React.Fragment key={student._id}>
                                            <ListItem
                                                button
                                                selected={selected}
                                                onClick={() => handleSelectStudent(student)}
                                                className={classes.studentRow}
                                            >
                                                <ListItemText
                                                    primary={
                                                        <Typography
                                                            className={classes.studentRowName}
                                                            style={{ fontWeight: hasUnread || selected ? 700 : 400 }}
                                                        >
                                                            {getStudentName(student)}
                                                        </Typography>
                                                    }
                                                    secondary={
                                                        (() => {
                                                            const presenceDisplay = isSchoolAdminRole
                                                                ? getStudentPresenceDisplay(student)
                                                                : null;
                                                            return (
                                                                <>
                                                                    <Typography className={classes.studentRowMeta}>
                                                                        {getClassName(student)}
                                                                    </Typography>
                                                                    {presenceDisplay && (
                                                                        <Typography
                                                                            className={`${classes.studentRowPresence}${
                                                                                presenceDisplay.status === 'online'
                                                                                    ? ` ${classes.studentRowPresenceOnline}`
                                                                                    : presenceDisplay.status === 'unavailable'
                                                                                        ? ` ${classes.studentRowPresenceUnavailable}`
                                                                                        : ` ${classes.studentRowPresenceOffline}`
                                                                            }`}
                                                                            title={presenceDisplay.meaning}
                                                                        >
                                                                            <span
                                                                                className={classes.presenceStatusDot}
                                                                                style={{
                                                                                    backgroundColor: PRESENCE_STATUS_MEANINGS[presenceDisplay.status]?.color || '#9e9e9e',
                                                                                }}
                                                                                aria-hidden
                                                                            />
                                                                            {presenceDisplay.text}
                                                                        </Typography>
                                                                    )}
                                                                </>
                                                            );
                                                        })()
                                                    }
                                                />
                                                {hasUnread && (
                                                    <span className={classes.studentRowDot} aria-label={`${unreadCount} new`}>
                                                        {formatNewCount(unreadCount)}
                                                    </span>
                                                )}
                                            </ListItem>
                                            <Divider />
                                        </React.Fragment>
                                    );
                                })
                            )}
                            </List>
                        </Box>
                    </Paper>

                    {/* Right Panel - Notes List */}
                    <Paper className={classes.notesPanel}>
                        <Box
                            p={2}
                            style={{
                                backgroundColor: '#f5f5f5',
                                borderBottom: '1px solid #e0e0e0',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 12,
                                flexWrap: 'wrap',
                            }}
                        >
                            <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
                                {selectedStudent ? `Notes from ${getStudentName(selectedStudent)}` : 'Select a student'}
                            </Typography>
                            {selectedStudent && (
                                <Box display="flex" alignItems="center" gridGap={12} flexWrap="wrap">
                                    {canComposeForStudent() && (
                                        <Button
                                            variant="contained"
                                            size="small"
                                            startIcon={<MessageIcon />}
                                            onClick={() => openComposeForStudent(selectedStudent)}
                                            style={{ backgroundColor: '#0F3B62', color: '#fff' }}
                                        >
                                            Write a message
                                        </Button>
                                    )}
                                </Box>
                            )}
                        </Box>
                        <Box className={classes.notesContainer}>
                            {!selectedStudent ? (
                                <Box style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                                    <Typography variant="body2">
                                        Please select a student from the left panel.
                                    </Typography>
                                </Box>
                            ) : notesList.length === 0 ? (
                                <Box style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                                    <Typography variant="body2">
                                        No messages from this student yet.
                                    </Typography>
                                </Box>
                            ) : (
                                notesList.map((note) => {
                                    const unreadCount = unreadCounts[String(note._id)] || 0;
                                    return (
                                        <Box
                                            key={note._id}
                                            className={`${classes.noteBubble} ${unreadCount > 0 ? classes.noteBubbleUnread : ''}`}
                                            onClick={() => handleOpenChat(note)}
                                            style={{ width: '100%' }}
                                        >
                                            <Box className={classes.noteHeader}>
                                                {note.noteType && (
                                                    <Chip
                                                        label="Submitted"
                                                        size="small"
                                                        style={{
                                                            backgroundColor: '#4caf50',
                                                            color: 'white',
                                                            fontSize: '0.65rem',
                                                            height: '18px',
                                                            fontWeight: 600,
                                                        }}
                                                    />
                                                )}
                                            </Box>
                                            {note.noteType && (
                                                <Typography className={classes.noteType}>
                                                    📋 {note.noteType}
                                                </Typography>
                                            )}
                                            {note.note && (
                                                <Typography className={classes.noteText}>
                                                    {note.note}
                                                </Typography>
                                            )}
                                            <Box className={classes.noteRowMeta}>
                                                <Typography className={classes.noteTime}>
                                                    {formatTime(note.createdAt)}
                                                </Typography>
                                                {unreadCount > 0 && (
                                                    <span className={classes.unreadDot} aria-label={`${unreadCount} new`}>
                                                        {formatNewCount(unreadCount)}
                                                    </span>
                                                )}
                                            </Box>
                                        </Box>
                                    );
                                })
                            )}
                        </Box>
                    </Paper>
                </Box>
            </Widget>

            {/* Chat Dialog */}
            <Dialog
                open={chatOpen}
                onClose={handleCloseChat}
                className={classes.chatDialog}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle className={classes.chatHeader}>
                    <Typography variant="h6" style={{ fontWeight: 'bold', color: 'white' }}>
                        {selectedNote ? `${getStudentNameFromNote(selectedNote)} • ${getClassNameFromNote(selectedNote)}` : 'Chat'}
                    </Typography>
                    <IconButton
                        aria-label="close"
                        onClick={handleCloseChat}
                        style={{ color: 'white' }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent className={classes.chatMessages}>
                    {chatMessages.length === 0 ? (
                        <Box style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                            <Typography variant="body2">
                                No messages yet. Start a conversation!
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            {chatMessages.map((m, idx) => {
                                const isFormNote = m.noteType && (m.messageKind !== 'CHAT' || !m.messageKind);
                                return (
                                    <Box
                                        key={m._id || idx}
                                        className={`${classes.chatMessageBubble} ${
                                            isMine(m) ? classes.messageSent : classes.messageReceived
                                        }`}
                                        style={isFormNote ? { backgroundColor: '#fff3cd', border: '1px solid #ffc107', color: '#856404' } : {}}
                                    >
                                        {isFormNote && (
                                            <Box style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                                                <Typography variant="caption" style={{ fontWeight: 700, fontSize: '0.7rem', opacity: 0.9 }}>
                                                    📋 {m.noteType}
                                                </Typography>
                                                <Chip
                                                    label="Submitted"
                                                    size="small"
                                                    style={{
                                                        backgroundColor: '#4caf50',
                                                        color: 'white',
                                                        fontSize: '0.65rem',
                                                        height: '18px',
                                                        fontWeight: 600,
                                                    }}
                                                />
                                            </Box>
                                        )}
                                        <Typography variant="body2">{m.note || '-'}</Typography>
                                        <Typography variant="caption" style={{ display: 'block', marginTop: 4, opacity: 0.75, fontSize: '0.7rem' }}>
                                            {formatTimeShort(m.createdAt)}
                                        </Typography>
                                    </Box>
                                );
                            })}
                            <div ref={chatMessagesEndRef} />
                        </>
                    )}
                </DialogContent>
                <DialogActions className={classes.chatInput}>
                    <TextField
                        fullWidth
                        variant="outlined"
                        placeholder="Type a message..."
                        value={chatInput}
                        onChange={(e) => setChatInput(e.target.value)}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter' && chatInput.trim()) {
                                handleSendMessage();
                            }
                        }}
                        size="small"
                    />
                    <IconButton
                        color="primary"
                        onClick={handleSendMessage}
                        disabled={!chatInput.trim()}
                    >
                        <SendIcon />
                    </IconButton>
                </DialogActions>
            </Dialog>

            <Dialog
                open={composeOpen}
                onClose={() => {
                    setComposeOpen(false);
                    setComposeStudent(null);
                    composeFormik.resetForm();
                }}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle style={{ fontWeight: 800 }}>
                    {composeStudent || selectedStudent
                        ? `Write a message — ${getStudentName(composeStudent || selectedStudent)}`
                        : 'Write a message'}
                </DialogTitle>
                <DialogContent>
                    <Card className={classes.composeCard}>
                        <CardContent>
                            <form id="note-list-compose-form" onSubmit={composeFormik.handleSubmit}>
                                <FormControl component="fieldset" fullWidth style={{ marginBottom: 24 }}>
                                    <FormLabel component="legend" className={classes.sectionTitle}>
                                        <span style={{ fontSize: 20 }}>📋</span>
                                        Note Type*
                                    </FormLabel>
                                    <RadioGroup
                                        name="noteType"
                                        value={composeFormik.values.noteType}
                                        onChange={composeFormik.handleChange}
                                        style={{ marginTop: 8 }}
                                    >
                                        <FormControlLabel
                                            value="Request for in person meeting "
                                            control={<Radio color="primary" />}
                                            label="Request for in person meeting "
                                        />
                                        <FormControlLabel
                                            value="Request for a call post school hours"
                                            control={<Radio color="primary" />}
                                            label="Request for a call post school hours"
                                        />
                                        <FormControlLabel
                                            value="Other"
                                            control={<Radio color="primary" />}
                                            label="Other"
                                        />
                                    </RadioGroup>
                                    {composeFormik.touched.noteType && composeFormik.errors.noteType && (
                                        <Typography variant="caption" style={{ color: '#d32f2f', marginTop: 6, display: 'block' }}>
                                            {composeFormik.errors.noteType}
                                        </Typography>
                                    )}
                                </FormControl>
                                <Box style={{ marginBottom: 16 }}>
                                    <FormLabel className={classes.sectionTitle} style={{ marginBottom: 8 }}>
                                        Message (Optional)
                                    </FormLabel>
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={4}
                                        name="note"
                                        value={composeFormik.values.note}
                                        onChange={composeFormik.handleChange}
                                        placeholder="Enter your message here..."
                                        variant="outlined"
                                        style={{ marginTop: 8 }}
                                    />
                                </Box>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    className={classes.yellowButton}
                                    disabled={!composeFormik.values.noteType || composeFormik.isSubmitting}
                                >
                                    SEND
                                </Button>
                            </form>
                        </CardContent>
                    </Card>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setComposeOpen(false);
                            setComposeStudent(null);
                            composeFormik.resetForm();
                        }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={toast.open}
                autoHideDuration={3000}
                onClose={() => setToast((prev) => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <SnackbarContent
                    style={{
                        backgroundColor: toast.type === 'success' ? '#2e7d32' : '#d32f2f',
                        color: '#fff',
                        fontWeight: 600,
                    }}
                    message={toast.message}
                />
            </Snackbar>
        </>
    );
}
